import { api } from './api';

/**
 * Configure le paiement pour un nouvel utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {Object} userData - Données de l'utilisateur
 * @param {string} userData.email - Email de l'utilisateur
 * @param {string} userData.name - Nom de l'utilisateur
 * @returns {Promise<Object>} - La réponse du serveur
 */
export const setupPayment = async (userId, { email, name }) => {
  try {
    
    const response = await api.post('/payments/setup', {
      email,
      name
    });
    
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erreur lors de la configuration du paiement');
    }
    
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la configuration du paiement:', {
      error: error.response?.data || error.message,
      status: error.response?.status
    });
    
    const errorMessage = error.response?.data?.message || 
      'Impossible de configurer le paiement. Veuillez réessayer.';
      
    throw new Error(errorMessage);
  }
};

/**
 * Crée une intention de configuration pour ajouter une méthode de paiement
 * @returns {Promise<Object>} - L'intention de configuration
 */
export const createSetupIntent = async () => {
  try {
    const response = await api.post('/payments/setup-intent');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création de l\'intention de configuration:', {
      error: error.response?.data || error.message,
      status: error.response?.status
    });
    throw new Error(error.response?.data?.message || 'Erreur lors de la configuration de la méthode de paiement');
  }
};

/**
 * Ajoute une méthode de paiement
 * @param {string} userId - ID de l'utilisateur
 * @param {Object} paymentMethodData - Données de la méthode de paiement
 * @param {string} paymentMethodData.paymentMethodId - ID de la méthode de paiement Stripe
 * @param {string} paymentMethodData.type - Type de méthode de paiement (ex: 'card')
 * @param {Object} paymentMethodData.card - Détails de la carte
 * @returns {Promise<Object>} - La méthode de paiement ajoutée
 */
export const addPaymentMethod = async (userId, { paymentMethodId, type, card }) => {
  try {
    
    // Envoyer les données au serveur
    const response = await api.post('/payments/payment-methods', {
      paymentMethodId,
      type,
      card
    });
    
    
    // Si la réponse est déjà un objet avec succès, la retourner directement
    if (response && response.success) {
      return response.paymentMethod || {
        id: paymentMethodId,
        type: type || 'card',
        card: card || {}
      };
    }
    
    // Si la réponse a une propriété data avec succès
    if (response.data && response.data.success) {
      return response.data.paymentMethod || {
        id: paymentMethodId,
        type: type || 'card',
        card: card || {}
      };
    }
    
    // Si la réponse est un tableau (cas où le backend renvoie directement la liste des méthodes)
    if (Array.isArray(response) || Array.isArray(response?.data)) {
      return {
        id: paymentMethodId,
        type: type || 'card',
        card: card || {}
      };
    }
    
    // Gérer les erreurs
    const errorMessage = response?.message || response?.data?.message || 'Erreur lors de l\'ajout de la méthode de paiement';
    throw new Error(errorMessage);
    
  } catch (error) {
    console.error('Erreur lors de l\'ajout de la méthode de paiement:', {
      error: error.response?.data || error.message,
      status: error.response?.status
    });
    
    const errorMessage = error.response?.data?.message || 
      'Impossible d\'ajouter la méthode de paiement. Veuillez réessayer.';
      
    throw new Error(errorMessage);
  }
};

/**
 * Récupère les méthodes de paiement de l'utilisateur
 * @returns {Promise<Array>} - Liste des méthodes de paiement
 */
export const getPaymentMethods = async () => {
  try {
    console.log('Appel à getPaymentMethods...');
    const response = await api.get('/payments/payment-methods');
    console.log('Réponse de getPaymentMethods:', response);
    
    // Vérifier si la réponse contient des données
    if (!response) {
      console.warn('Aucune réponse du serveur');
      return [];
    }
    
    // Si la réponse est un tableau, le retourner directement
    if (Array.isArray(response)) {
      return response;
    }
    
    // Si la réponse contient une propriété data qui est un tableau
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Si la réponse contient une propriété paymentMethods
    if (response.paymentMethods && Array.isArray(response.paymentMethods)) {
      return response.paymentMethods;
    }
    
    // Si la réponse contient une propriété data.paymentMethods
    if (response.data && response.data.paymentMethods && Array.isArray(response.data.paymentMethods)) {
      console.log('Méthodes de paiement trouvées dans response.data.paymentMethods');
      return response.data.paymentMethods;
    }
    
    console.warn('Format de réponse non reconnu pour les méthodes de paiement. Réponse complète:', {
      response,
      data: response.data,
      isArrayResponse: Array.isArray(response),
      hasData: !!response.data,
      dataIsArray: response.data && Array.isArray(response.data)
    });
    return [];
    
  } catch (error) {
    console.error('Erreur lors de la récupération des méthodes de paiement:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      config: error.config
    });
    
    // Si l'erreur 404, retourner un tableau vide (aucune méthode enregistrée)
    if (error.response?.status === 404) {
      return [];
    }
    
    // Si l'utilisateur n'est pas authentifié
    if (error.response?.status === 401) {
      console.error('Non authentifié - Redirection vers la page de connexion');
      // Vous pourriez vouloir déclencher une déconnexion ici
      // auth.logout();
      throw new Error('Votre session a expiré. Veuillez vous reconnecter.');
    }
    
    throw new Error(
      error.response?.data?.message || 
      'Impossible de charger les méthodes de paiement. Veuillez réessayer plus tard.'
    );
  }
};

/**
 * Supprime une méthode de paiement
 * @param {string} paymentMethodId - ID de la méthode de paiement à supprimer
 * @returns {Promise<Object>} - La réponse du serveur
 */
export const removePaymentMethod = async (paymentMethodId) => {
  try {
    const response = await api.delete(`/payments/payment-methods/${paymentMethodId}`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression de la méthode de paiement:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la suppression de la méthode de paiement');
  }
};

/**
 * Effectue un paiement
 * @param {Object} paymentData - Données de paiement
 * @param {string} paymentData.amount - Montant du paiement
 * @param {string} paymentData.currency - Devise (par défaut: 'EUR')
 * @param {string} paymentData.paymentMethodId - ID de la méthode de paiement
 * @returns {Promise<Object>} - Le résultat du paiement
 */
export const processPayment = async (paymentData) => {
  try {
    const response = await api.post('/payments/create-payment-intent', paymentData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du traitement du paiement:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors du traitement du paiement');
  }
};

/**
 * Récupère l'historique des transactions
 * @param {string} userId - ID de l'utilisateur
 * @param {Object} params - Paramètres de pagination
 * @param {number} params.page - Numéro de page
 * @param {number} params.limit - Nombre d'éléments par page
 * @returns {Promise<Object>} - Liste des transactions et métadonnées
 */
export const getTransactionHistory = async (userId, { page = 1, limit = 10 } = {}) => {
  try {
    // Utilisation du même schéma que getPaymentStats
    const response = await api.get(`/payments/${userId}/transactions?page=${page}&limit=${limit}`);
    
    if (!response || !response.data) {
      throw new Error('Réponse invalide du serveur');
    }
    
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique des transactions:', error);
    throw new Error(
      error.response?.data?.message || 'Erreur lors de la récupération de l\'historique des transactions'
    );
  }
};

/**
 * Demande un virement
 * @param {number} amount - Montant à virer
 * @param {string} paymentMethodId - ID de la méthode de paiement
 * @returns {Promise<Object>} - Résultat de la demande de virement
 */
export const requestPayout = async (amount, paymentMethodId) => {
  try {
    const response = await api.post('/payments/payouts', { amount, paymentMethodId });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la demande de virement:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la demande de virement');
  }
};

/**
 * Récupère les statistiques de paiement
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - Statistiques de paiement
 */
export const getPaymentStats = async (userId) => {
  try {
    const response = await api.get(`/payments/${userId}/stats`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques de paiement:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des statistiques de paiement');
  }
};




/**
 * Effectue un paiement d'abonnement
 * @param {Object} subscriptionData - Données de l'abonnement
 * @param {string} subscriptionData.amount - Montant de l'abonnement
 * @param {string} subscriptionData.currency - Devise (par défaut: 'EUR')
 * @param {string} subscriptionData.paymentMethodId - ID de la méthode de paiement
 * @param {string} subscriptionData.subscriptionId - ID de l'abonnement dans la base de données
 * @returns {Promise<Object>} - Le résultat du paiement
 */
export const processSubscriptionPayment = async ({ amount, currency = 'EUR', paymentMethodId, subscriptionId }) => {
  try {
    
    const response = await api.post('/payments/create-payment-intent', {
      amount,
      currency,
      paymentMethodId,
      isSubscription: true,
      subscriptionId
    });

    const responseData = response.data || response;
    
    if (!responseData.success) {
      throw new Error(responseData.message || 'Erreur lors du traitement du paiement d\'abonnement');
    }
    
    return responseData;
  } catch (error) {
    console.error('Erreur lors du traitement du paiement d\'abonnement:', {
      error: error.response?.data || error.message,
      status: error.response?.status
    });
    
    const errorMessage = error.response?.data?.message || 
      'Impossible de traiter le paiement d\'abonnement. Veuillez réessayer.';
      
    throw new Error(errorMessage);
  }
};

/**
 * Récupère les détails d'une transaction spécifique
 * @param {string} transactionId - ID de la transaction
 * @returns {Promise<Object>} - Détails de la transaction
 */
export const getTransactionDetails = async (transactionId) => {
  
  try {
    if (!transactionId) {
      throw new Error('ID de transaction manquant');
    }

    const response = await api.get(`/payments/transaction/${transactionId}`);
    
    
    if (!response) {
      throw new Error('Aucune réponse du serveur');
    }
    
    if (!response.data) {
      throw new Error('Données de réponse manquantes');
    }
    
    if (response.data.success === false) {
      const errorMessage = response.data.message || 'Erreur lors de la récupération des détails de la transaction';
      console.error(`[getTransactionDetails] Erreur du serveur: ${errorMessage}`);
      throw new Error(errorMessage);
    }
    
    return response.data;
    
  } catch (error) {
    console.error('[getTransactionDetails] Erreur détaillée:', {
      message: error.message,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data
      } : 'Pas de réponse',
      stack: error.stack
    });
    
    // Si l'erreur contient une réponse du serveur avec un message d'erreur, on l'utilise
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Erreur lors de la récupération des détails de la transaction';
    
    throw new Error(errorMessage);
  }
};

// Exportation par défaut pour la rétrocompatibilité
export default {
  setupPayment,
  createSetupIntent,
  addPaymentMethod,
  getPaymentMethods,
  removePaymentMethod,
  processPayment,
  getTransactionHistory,
  requestPayout,
  getPaymentStats,
  processSubscriptionPayment,
  getTransactionDetails
};
